import { CardWithHeader } from "@/components/card-with-header";
import {
  ComplianceCaseFilters,
  ComplianceCaseFiltersValue,
} from "@/components/compliance/case-filters";
import { CaseStatusSummary } from "@/components/compliance/case-status-summary";
import { RefreshIconButton } from "@/components/refresh-icon-button";
import { RequirementsNeededTree } from "@/components/compliance/requirements-needed-tree";
import { formatTransactionVolumeWithCurrency } from "@/components/compliance/admin-case-detail.utils";
import { CaseStatusOrWorkflowBadge } from "@/components/compliance/status-badge";
import { Spinner } from "@/components/ui/spinner";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useAnalytics } from "@/lib/analytics";
import { parseCommaList } from "@/components/compliance/case-filters.utils";
import { useMerchantComplianceCasesList } from "@/hooks/useMerchantComplianceCasesList";
import { cn } from "@/lib/utils";
import { zodValidator } from "@tanstack/zod-adapter";
import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowRight, Info } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { z } from "zod/v4";
import { useCanSeeTmBackedComplianceScreen } from "./-components/is-compliance-enabled";
import { TransactionMonitoringCaseListRoute } from "../_transaction-monitoring/-components/transaction-monitoring-list-route";

const searchParamsSchema = z.object({
  applicationId: z.string().optional(),
  workflowStatuses: z.string().optional(),
});

export const Route = createFileRoute(
  "/(app)/_main-layout/_compliance/compliance/",
)({
  component: MerchantComplianceIndex,
  validateSearch: zodValidator(searchParamsSchema),
});

function MerchantComplianceIndex() {
  const tmBackedCompliance = useCanSeeTmBackedComplianceScreen();
  if (tmBackedCompliance) {
    return <TransactionMonitoringCaseListRoute statusFilter="all" />;
  }
  return <FxMerchantComplianceIndex />;
}

function FxMerchantComplianceIndex() {
  const context = Route.useRouteContext();
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const analytics = useAnalytics();
  const loadMoreRef = useRef<HTMLTableRowElement>(null);

  const filters: ComplianceCaseFiltersValue = {
    workflowStatuses: search.workflowStatuses,
  };

  const casesQuery = useMerchantComplianceCasesList({
    accessToken: context.accessToken,
    applicationId: context.selectedApplicationId,
    workflowStatuses: search.workflowStatuses,
  });

  const cases = casesQuery.cases ?? [];

  const [previousWorkflowStatuses, setPreviousWorkflowStatuses] = useState<
    string[]
  >([]);
  useEffect(() => {
    if (!casesQuery.isFetching) {
      setPreviousWorkflowStatuses(
        parseCommaList(search.workflowStatuses ?? ""),
      );
    }
  }, [casesQuery.isFetching, search.workflowStatuses]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const first = entries[0];
        if (
          first?.isIntersecting &&
          casesQuery.hasNextPage &&
          !casesQuery.isFetchingNextPage
        ) {
          void casesQuery.fetchNextPage();
        }
      },
      {
        threshold: 0.1,
        rootMargin: "100px",
      },
    );

    const currentRef = loadMoreRef.current;
    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, [
    casesQuery.fetchNextPage,
    casesQuery.hasNextPage,
    casesQuery.isFetchingNextPage,
  ]);

  return (
    <CardWithHeader
      variant="exchange"
      label="Compliance"
      title="Track and manage compliance cases"
      headerContent={
        <RefreshIconButton
          label="Refresh compliance cases"
          onRefresh={() => {
            analytics.track("compliance_list_refreshed");
            void casesQuery.refresh();
          }}
        />
      }
    >
      <div className="mb-4 flex gap-3 rounded-xl border border-blue-200 bg-blue-50/60 p-4 text-sm text-blue-900">
        <Info className="mt-0.5 size-4 shrink-0 text-blue-500" />
        <div className="space-y-2">
          <p>
            These are documentation requirements that need to be collected for
            your customers on behalf of our banking partners. Failure to provide
            the required documentation in a timely manner may result in account
            suspension or restriction of money transfer services.
          </p>
          <p>
            After uploading documents, submit each case for review so they can
            be processed by the Volume compliance team.
          </p>
        </div>
      </div>

      <CaseStatusSummary />

      <section className="rounded-2xl border border-volume-text-purple/20 bg-gradient-to-br from-volume-light-purple/15 to-slate-50 p-3">
        <ComplianceCaseFilters
          value={filters}
          isFetching={casesQuery.isFetching}
          previousWorkflowStatuses={previousWorkflowStatuses}
          onChange={(next) => {
            analytics.track("compliance_filter_applied", {
              workflow_statuses: next.workflowStatuses,
            });
            navigate({
              to: ".",
              search: {
                ...search,
                workflowStatuses: next.workflowStatuses,
              },
            });
          }}
        />
      </section>

      <div className="mt-4 relative overflow-hidden rounded-2xl border border-volume-text-purple/20 bg-white">
        <div className="relative overflow-x-auto">
          <Table className="bg-white">
            <TableHeader className="bg-slate-50/70">
              <TableRow className="border-slate-200">
                <TableHead className="h-8 min-w-0 px-2 py-1.5 text-xs font-medium text-slate-600">
                  Customer
                </TableHead>
                <TableHead className="h-8 min-w-0 px-2 py-1.5 text-xs font-medium text-slate-600">
                  Status
                </TableHead>
                <TableHead className="h-8 min-w-0 px-2 py-1.5 text-xs font-medium text-slate-600">
                  Volume
                </TableHead>
                <TableHead className="h-8 min-w-0 px-2 py-1.5 text-xs font-medium text-slate-600">
                  Requirements
                </TableHead>
                <TableHead className="sticky right-0 h-8 w-12 shrink-0 bg-slate-50/95 px-2 py-1.5 text-right text-xs font-medium text-slate-600 backdrop-blur-[1px]">
                  <span className="sr-only">Actions</span>
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {casesQuery.isLoading ? (
                <TableRow className="border-slate-200">
                  <TableCell colSpan={5}>
                    <div className="flex items-center justify-center gap-3 py-6 text-sm text-slate-600">
                      <Spinner width={20} height={20} />
                      <span>Loading compliance cases...</span>
                    </div>
                  </TableCell>
                </TableRow>
              ) : null}

              {casesQuery.isError ? (
                <TableRow className="border-slate-200">
                  <TableCell colSpan={5}>
                    <div className="py-6 text-center text-sm text-red-700">
                      <p className="font-medium">
                        Failed to load compliance cases.
                      </p>
                      <p className="mt-1 text-red-600">
                        {casesQuery.error instanceof Error
                          ? casesQuery.error.message
                          : "An unexpected error occurred"}
                      </p>
                    </div>
                  </TableCell>
                </TableRow>
              ) : null}

              {!casesQuery.isLoading &&
              !casesQuery.isError &&
              cases.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={5}
                    className="py-8 text-center text-sm text-slate-600"
                  >
                    No compliance cases found.
                  </TableCell>
                </TableRow>
              ) : null}

              {cases.map((item) => {
                return (
                  <TableRow
                    key={item.id}
                    className={cn(
                      "group border-slate-200 hover:bg-volume-light-purple/10",
                    )}
                  >
                    <TableCell className="min-w-[80px] max-w-[140px] truncate px-2 py-1.5">
                      <div className="truncate text-sm font-medium text-slate-900">
                        {item.customerName || (
                          <span className="text-slate-400 italic">
                            Unknown customer
                          </span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="min-w-0 px-2 py-1.5">
                      <CaseStatusOrWorkflowBadge
                        status={item.status}
                        workflowStatus={item.workflowStatus}
                      />
                    </TableCell>
                    <TableCell className="px-2 py-1.5 text-sm text-slate-700">
                      {item.transactionVolume != null ||
                      item.transactionCount != null ? (
                        <div className="flex flex-col">
                          {item.transactionVolume != null ? (
                            <span>
                              {formatTransactionVolumeWithCurrency(
                                item.transactionVolume,
                                item.transactionVolumeCurrency ?? "",
                              )}
                            </span>
                          ) : null}
                          {item.transactionCount != null ? (
                            <span className="text-slate-500">
                              {item.transactionCount} transaction
                              {item.transactionCount !== 1 ? "s" : ""}
                            </span>
                          ) : null}
                        </div>
                      ) : (
                        <span className="text-slate-400">—</span>
                      )}
                    </TableCell>
                    <TableCell className="min-w-0 max-w-[140px] px-2 py-1.5">
                      <RequirementsNeededTree
                        requirements={item.requirements ?? []}
                      />
                    </TableCell>
                    <TableCell
                      className={cn(
                        "sticky right-0 w-12 shrink-0 px-2 py-1.5 text-right shadow-[-4px_0_8px_-2px_rgba(0,0,0,0.04)]",
                        "bg-white group-hover:bg-volume-light-purple/10",
                      )}
                    >
                      <Link
                        to="/compliance/$caseId"
                        params={{ caseId: item.id }}
                        search={
                          typeof search.applicationId === "string"
                            ? { applicationId: search.applicationId }
                            : {}
                        }
                        preload="intent"
                        onClick={() =>
                          analytics.track("compliance_case_clicked", {
                            case_id: item.id,
                          })
                        }
                      >
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-7 gap-1 px-2 text-xs font-medium text-volume-text-purple hover:bg-volume-light-purple/40"
                        >
                          View
                          <ArrowRight className="size-3.5" />
                        </Button>
                      </Link>
                    </TableCell>
                  </TableRow>
                );
              })}
              {casesQuery.hasNextPage || casesQuery.isFetchingNextPage ? (
                <TableRow ref={loadMoreRef}>
                  <TableCell colSpan={5} className="py-8 text-center">
                    {casesQuery.isFetchingNextPage ? (
                      <div className="flex items-center justify-center gap-2">
                        <Spinner width={20} height={20} />
                        <span className="text-sm text-slate-600">
                          Loading more compliance cases...
                        </span>
                      </div>
                    ) : (
                      <div className="h-20" />
                    )}
                  </TableCell>
                </TableRow>
              ) : cases.length > 0 ? (
                <TableRow>
                  <TableCell colSpan={5} className="py-6 text-center">
                    <span className="text-sm text-slate-500">
                      No more cases to load
                    </span>
                  </TableCell>
                </TableRow>
              ) : null}
            </TableBody>
          </Table>
        </div>
      </div>
    </CardWithHeader>
  );
}
